import { useCallback, useMemo, useState } from 'react';
import {
  ICancellationReason,
  isOtherCancellationReason,
  validateOtherCancellationReason,
} from '~/domain/JobCancellation';

export interface ICancellationSurveyAnswer {
  readonly reason: ICancellationReason;
  /** Trimmed "Other" text; '' for every other reason. */
  readonly otherText: string;
}

export const useCancellationSurveyForm = (reasons: readonly ICancellationReason[]) => {
  const [selectedCode, setSelectedCode] = useState<string>();
  const [otherText, setOtherText] = useState('');
  const [isOtherTextTouched, setIsOtherTextTouched] = useState(false);

  const selectedReason = useMemo(
    () => reasons.find((reason) => reason.code === selectedCode) ?? null,
    [reasons, selectedCode],
  );
  const isOther = isOtherCancellationReason(selectedReason);
  const validation = useMemo(() => validateOtherCancellationReason(otherText), [otherText]);

  // Every error, too-long included, waits for the first blur (mobile GOTU-15984 does the same).
  const otherTextError =
    isOther && isOtherTextTouched && !validation.valid ? validation.error : undefined;

  const canSubmit = selectedReason !== null && (!isOther || validation.valid);

  const answer = useMemo<ICancellationSurveyAnswer | null>(() => {
    if (!selectedReason || !canSubmit) return null;
    return { reason: selectedReason, otherText: isOther ? otherText.trim() : '' };
  }, [canSubmit, isOther, otherText, selectedReason]);

  // The DS select fires onChange for the option that's already picked, which mustn't wipe the text.
  const selectReason = useCallback(
    (code: string) => {
      if (code === selectedCode) return;
      setSelectedCode(code);
      setOtherText('');
      setIsOtherTextTouched(false);
    },
    [selectedCode],
  );

  const touchOtherText = useCallback(() => setIsOtherTextTouched(true), []);

  return {
    selectedCode: selectedReason?.code,
    isOther,
    otherText,
    otherTextError,
    canSubmit,
    answer,
    selectReason,
    changeOtherText: setOtherText,
    touchOtherText,
  };
};
